import { createSlice } from "@reduxjs/toolkit"

import { parseToObject } from "../../../utils/formatters"

import {
  deleteFormSavedData,
  fetchFormByCaseIdWithMergeData,
  fetchFormByCaseIdWithMergeDataRefinement,
  fetchFormLists,
  saveDefaultFormData,
  saveUassistSummary,
  updateFormSavedData,
  getSmartTemplateData,
  warmFormByCaseId,
} from "./formThunkApi"
import FORM_CONSTANT from "./form.constant"
import { buildFormCacheKey } from "./form.util"
import type { getFormByIdWithMergeData } from "./form.Service"

// The actual shape warmFormByCaseId resolves to (it just returns
// getFormByIdWithMergeData's result directly), rather than a hand-picked type -
// stays correct if that function's return shape ever changes.
type WarmFormByCaseIdResponse = Awaited<ReturnType<typeof getFormByIdWithMergeData>>

interface IData {
  [key: string]: unknown
}
interface IRxForm {
  schema: IData
  uischema: IData
  data: IData
  name: string
  id: string
  saveid: string
}

interface refinementIRXForm {
  schema: IData
  uischema: IData
  data: IData
  name: string
  id: string
  saveid: string
  course_check_number: string
}

interface IFormState {
  formTemplateList: SmartRxForm[]
  isLoading: "idle" | "pending" | "succeeded" | "failed"
  rxJsonForm: IRxForm
  rxPrescription: IRxForm
  rxRefinementFormAdd: refinementIRXForm
  rxRefinementFormEdit: refinementIRXForm
  onHoldMsg?: string
  isRecordUpdated?: boolean
  formWarmCache: {
    key: string | null
    response: WarmFormByCaseIdResponse | null
    status: "idle" | "pending" | "succeeded" | "failed"
  }
  /**
   * Persists any Form.tsx fetch response (main aligner form or a nested SmartRx
   * sub-form like crowding/open bite), keyed by buildFormCacheKey's identity string.
   * Kept in redux - rather than a component's local state - so it survives a Form
   * instance unmounting and remounting with the same identity, which happens on every
   * prescription<->summary route change (each is a separate Route match, so React
   * fully unmounts one Form/FormProvider tree and mounts a fresh one for the other).
   * Nested SmartRx sub-forms (rendered via USmartRXForm) have their own Form instance
   * per template and aren't covered by formWarmCache, which is single-slot and only
   * ever warmed for the outer aligner form from Scans/CaseType.
   */
  formResponseCache: Record<string, WarmFormByCaseIdResponse>
}

const initialState: IFormState = {
  rxJsonForm: {
    schema: {},
    uischema: {},
    data: {},
    name: "",
    id: "",
    saveid: "",
  },

  formTemplateList: [],
  isLoading: "idle",
  formWarmCache: {
    key: null,
    response: null,
    status: "idle",
  },
  rxPrescription: {
    schema: {},
    uischema: {},
    data: {},
    name: "",
    id: "",
    saveid: "",
  },
  rxRefinementFormAdd: {
    schema: {},
    uischema: {},
    data: {},
    name: "",
    id: "",
    saveid: "",
    course_check_number: "",
  },
  rxRefinementFormEdit: {
    schema: {},
    uischema: {},
    data: {},
    name: "",
    id: "",
    saveid: "",
    course_check_number: "",
  },
  onHoldMsg: null,
  isRecordUpdated: false,
  formResponseCache: {},
}

export const formServiceSlice = createSlice({
  name: "formService",
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = "pending"
    },
    resetSchema: (state) => {
      state.rxJsonForm = {
        schema: {},
        uischema: {},
        data: {},
        name: "",
        id: "",
        saveid: "",
      }
    },
    resetPrescriptionSchema: (state) => {
      state.rxPrescription = {
        schema: {},
        uischema: {},
        data: {},
        name: "",
        id: "",
        saveid: "",
      }
    },
    resetRxRefinementFormEdit: (state) => {
      state.rxRefinementFormEdit = {
        schema: {},
        uischema: {},
        data: {},
        name: "",
        id: "",
        saveid: "",
      }
    },
    resetRxRefinementFormAdd: (state) => {
      state.rxRefinementFormAdd = {
        schema: {},
        uischema: {},
        data: {},
        name: "",
        id: "",
        saveid: "",
      }
    },
    updateRxFormSchema: (state, action) => {
      state.rxJsonForm.schema.properties.arches_to_treat = {
        ...action.payload,
      }
    },
    updateSchema: (state, action) => {
      state.rxPrescription.schema = {
        ...state.rxPrescription.schema,
        properties: action.payload
      }
    },

    updatePrescriptionData: (state, action) => {
      state.rxPrescription.data = {
        ...state.rxPrescription.data,
        ...action.payload,
      }
    },
    updatePrescriptionSchema: (state, action) => {
      state.rxPrescription.schema.properties.arches_to_treat.properties = {
        ...action.payload,
      }
    },
    updatePrescriptionSchemaArches: (state, action) => {
      if(!state.rxPrescription.schema.properties) return
      state.rxPrescription.schema.properties.arches_to_treat = {
        ...action.payload,
      }
    },
    updaterxFormData: (state, action) => {
      state.rxJsonForm.data = {
        ...state.rxJsonForm.data,
        ...action.payload,
      }
    },
    updaterxFormDataAdd: (state, action) => {
      state.rxRefinementFormAdd.data = {
        ...state.rxRefinementFormAdd.data,
        ...action.payload,
      }
    },
    updaterxFormDataEdit: (state, action) => {
      state.rxRefinementFormEdit.data = {
        ...state.rxRefinementFormEdit.data,
        ...action.payload,
      }
    },
    updateOnHoldMsg: (state, action) => {
      state.onHoldMsg = action.payload
    },
    setIsRecordsUpdated: (state, action) => {
      state.isRecordUpdated = action.payload
    },
    cacheFormResponse: (state, action: { payload: { key: string; response: WarmFormByCaseIdResponse } }) => {
      state.formResponseCache[action.payload.key] = action.payload.response
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchFormLists.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(fetchFormLists.fulfilled, (state, action) => {
      const result = action.payload.data.Result
      state.formTemplateList = Object.keys(result).map((key: string) => {
        return result[key]
      })
      state.isLoading = "succeeded"
    })
    builder.addCase(fetchFormLists.rejected, (state) => {
      state.isLoading = "failed"
    })
    builder.addCase(saveDefaultFormData.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(saveDefaultFormData.fulfilled, (state, action) => {
      state.isLoading =
        action.payload.status === "success" ? "succeeded" : "failed"
    })
    builder.addCase(saveDefaultFormData.rejected, (state) => {
      state.isLoading = "failed"
    })

    builder.addCase(updateFormSavedData.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(updateFormSavedData.fulfilled, (state, action) => {
      state.isLoading = "succeeded"
      const savedData = action.payload.data.result
      if (action.payload.data.status === "success") {
        state.rxJsonForm.saveid = savedData.id
        if (savedData.saved_data) {
          state.rxJsonForm.savedData = parseToObject(savedData.saved_data)
        }
        // A save just changed what's persisted for this form, so any cached
        // fetch response for it (formResponseCache/formWarmCache) is stale -
        // drop it so the next mount re-fetches instead of showing pre-save
        // data. Matched by formId:caseId prefix since this thunk doesn't carry
        // patientId/uassistRound to build the exact buildFormCacheKey string.
        const { formId, caseId } = action.meta.arg
        const staleKeyPrefix = `${formId}:${caseId}:`
        Object.keys(state.formResponseCache).forEach((key) => {
          if (key.startsWith(staleKeyPrefix)) delete state.formResponseCache[key]
        })
        if (state.formWarmCache.key?.startsWith(staleKeyPrefix)) {
          state.formWarmCache = { key: null, response: null, status: "idle" }
        }
      }
    })
    builder.addCase(updateFormSavedData.rejected, (state) => {
      state.isLoading = "failed"
    })
    builder.addCase(fetchFormByCaseIdWithMergeData.pending, (state) => {
      state.isLoading = "pending"
      state.rxJsonForm = {
        schema: {},
        uischema: {},
        data: {},
        name: "",
        id: "",
        saveid: "",
        savedData: {},
      }
    })
    builder.addCase(
      fetchFormByCaseIdWithMergeData.fulfilled,
      (state, action) => {
        state.isLoading = "succeeded"
        const {
          default_data,
          form_schema,
          ui_schema,
          save_id,
          saved_data,
          uassist_round,
        } = action?.payload?.formResponse || {
          default_data: {},
          form_schema: {},
          ui_schema: {},
          save_id: "",
          saved_data: {},
          uassist_round: null,
        }
        const obj = {
          schema: parseToObject(form_schema?.schema_data || {}),
          uischema: parseToObject(ui_schema?.schema_data || {}),
          data: default_data ? parseToObject(default_data) : {},
          name: form_schema?.name || "",
          id: form_schema?.id || "",
          saveid: save_id || "",
          uassistRound: uassist_round,
        }
        const isUassistForm = obj?.name &&
          Object.values(FORM_CONSTANT)
            .map(value => typeof value === "string" ? value.toLowerCase() : "")
            .includes(obj.name.toLowerCase())
        if (isUassistForm) {
          state.rxPrescription = obj
        } else {
          state.rxJsonForm = obj
        }
      },
    )
    builder.addCase(fetchFormByCaseIdWithMergeData.rejected, (state) => {
      state.isLoading = "failed"
    })

    // Warm-cache for the Forms page: intentionally does NOT touch state.isLoading,
    // since that field feeds App.tsx's app-wide loading overlay.
    builder.addCase(warmFormByCaseId.pending, (state, action) => {
      state.formWarmCache = {
        key: buildFormCacheKey(action.meta.arg),
        response: null,
        status: "pending",
      }
    })
    builder.addCase(warmFormByCaseId.fulfilled, (state, action) => {
      state.formWarmCache = {
        key: buildFormCacheKey(action.meta.arg),
        response: action.payload,
        status: "succeeded",
      }
    })
    builder.addCase(warmFormByCaseId.rejected, (state, action) => {
      state.formWarmCache = {
        key: buildFormCacheKey(action.meta.arg),
        response: null,
        status: "failed",
      }
    })

    // refinement
    builder.addCase(
      fetchFormByCaseIdWithMergeDataRefinement.pending,
      (state) => {
        state.isLoading = "pending"
        // state.rxRefinementForm = {
        //   schema: {},
        //   uischema: {},
        //   data: {},
        //   name: "",
        //   id: "",
        //   saveid: "",
        //   savedData: {},
        //   course_check_number: "",
        // }
      },
    )
    builder.addCase(
      fetchFormByCaseIdWithMergeDataRefinement.fulfilled,
      (state, action) => {
        state.isLoading = "succeeded"
        const { isAdd, dataObj } = action.payload
        const {
          default_data,
          form_schema,
          ui_schema,
          save_id,
          saved_data,
          course_check_number,
        } = dataObj
        const obj = {
          schema: parseToObject(form_schema.schema_data),
          uischema: parseToObject(ui_schema.schema_data),
          data: default_data ? parseToObject(default_data) : {},
          name: form_schema.name,
          id: form_schema.id,
          saveid: save_id || "",
        }
        if (isAdd) {
          state.rxRefinementFormAdd = obj
        } else {
          state.rxRefinementFormEdit = obj
        }
      },
    )
    builder.addCase(
      fetchFormByCaseIdWithMergeDataRefinement.rejected,
      (state) => {
        state.isLoading = "failed"
      },
    )

    builder.addCase(saveUassistSummary.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(saveUassistSummary.fulfilled, (state) => {
      state.isLoading = "succeeded"
    })
    builder.addCase(saveUassistSummary.rejected, (state) => {
      state.isLoading = "failed"
    })
    builder.addCase(deleteFormSavedData.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(deleteFormSavedData.fulfilled, (state, action) => {
      state.isLoading = "succeeded"
    })
    builder.addCase(deleteFormSavedData.rejected, (state) => {
      state.isLoading = "failed"
    })
    builder.addCase(getSmartTemplateData.pending, (state) => {
      state.isLoading = "pending"
    })
    builder.addCase(getSmartTemplateData.fulfilled, (state, action) => {
      state.isLoading = "succeeded"
    })
    builder.addCase(getSmartTemplateData.rejected, (state) => {
      state.isLoading = "failed"
    })
  },
})

export const {
  resetSchema,
  updatePrescriptionData,
  updatePrescriptionSchema,
  updatePrescriptionSchemaArches,
  updaterxFormData,
  updaterxFormDataAdd,
  updaterxFormDataEdit,
  resetPrescriptionSchema,
  resetRxRefinementFormEdit,
  resetRxRefinementFormAdd,
  startLoading,
  updateRxFormSchema,
  updateSchema,
  updateOnHoldMsg,
  setIsRecordsUpdated,
  cacheFormResponse,
} = formServiceSlice.actions
